import { useAuthenticatedSession } from '@/authenticatedSession/AuthenticatedSessionContext';
import {
  HumanData,
  HumanDocument,
  MatterDocument,
  OrganizationData,
  OrganizationDocument,
} from '@/database/baseTypes';
import { useMatters } from '@/database/hooks/matters';
import { OrderByClause, WhereClause } from '@/database/queryBuilder';
import {
  Query,
  getDocs,
  limit,
  onSnapshot,
  query,
  startAfter,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

/**
 * WARNING: This function was generated by Claude and is not yet fully tested.
 */
export const usePeopleContacts = (
  page = 1,
  pageSize = 50,
  filters: WhereClause<HumanData>[] = [],
  sortBy: OrderByClause<HumanData>[] = [],
) => {
  const authSession = useAuthenticatedSession();
  const [humans, setHumans] = useState<HumanDocument[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [matters, setMatters] = useState<MatterDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get the total count for pagination
  useEffect(() => {
    if (!authSession) return;

    const fetchTotalCount = async () => {
      try {
        // We need to apply the same filters to get accurate total count
        const snapshot = await authSession.db.getHumans(filters);
        setTotalCount(snapshot.size);
      } catch (error) {
        console.error('Error fetching total count:', error);
      }
    };

    fetchTotalCount();
  }, [authSession, JSON.stringify(filters)]);

  // Fetch the paginated data
  useEffect(() => {
    if (!authSession) return;
    setIsLoading(true);

    // Determine sort order with fallback to lastName
    const effectiveSortBy =
      sortBy.length > 0
        ? sortBy
        : [['lastName', 'asc'] as OrderByClause<HumanData>];

    // Calculate pagination limits for Firestore
    const pageSizeLimit = pageSize;
    const startAt = (page - 1) * pageSize;

    // Apply pagination
    if (startAt > 0) {
      // For Firestore, we need to get a document to use as a starting point
      // This is a simplified approach that works for basic sorting
      let humansQuery: Query<HumanData> = authSession.db.query.humans(
        filters,
        effectiveSortBy,
      );
      humansQuery = query(humansQuery, limit(startAt));
      getDocs(humansQuery)
        .then((snapshot) => {
          const lastVisible = snapshot.docs[snapshot.docs.length - 1];

          if (lastVisible) {
            // If we have a starting point, get the next page
            let paginatedQuery: Query<HumanData> = authSession.db.query.humans(
              filters,
              effectiveSortBy,
            );
            paginatedQuery = query(paginatedQuery, startAfter(lastVisible));
            paginatedQuery = query(paginatedQuery, limit(pageSizeLimit));

            const unsubscribeHumans = onSnapshot(
              paginatedQuery,
              (snapshot) => {
                const humanData = snapshot.docs.map((doc) => ({
                  ...doc.data(),
                  id: doc.id,
                }));
                setHumans(humanData);
                setIsLoading(false);
              },
              (error) => {
                console.error('Error fetching humans:', error);
                setIsLoading(false);
              },
            );

            return () => unsubscribeHumans();
          } else {
            // If there are no results in the previous pages, return empty
            setHumans([]);
            setIsLoading(false);
            return () => {};
          }
        })
        .catch((error) => {
          console.error('Error with pagination:', error);
          setIsLoading(false);
          return () => {};
        });
    } else {
      // First page, no need for starting point
      const unsubscribeHumans = onSnapshot(
        query(
          authSession.db.query.humans(filters, effectiveSortBy),
          limit(pageSizeLimit),
        ),

        (snapshot) => {
          const humanData = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          setHumans(humanData);
          setIsLoading(false);
        },
        (error) => {
          console.error('Error fetching humans:', error);
          setIsLoading(false);
        },
      );

      const unsubscribeMatters = authSession.db.subscribeToMattersChanges(
        (snapshot) => {
          setMatters(
            snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
          );
        },
      );

      return () => {
        unsubscribeHumans();
        unsubscribeMatters();
      };
    }
  }, [
    authSession,
    page,
    pageSize,
    JSON.stringify(filters),
    JSON.stringify(sortBy),
  ]);

  const humansWithMatters = useMemo(() => {
    // Count only matters where the contact is a client, mirroring the
    // ClientDetailDrawer's filter. Otherwise the row's count and the drawer's
    // matter list disagree (e.g. a row showing "5" but the drawer listing 3
    // because the other 2 are adverse-party engagements).
    return humans.map((human) => ({
      ...human,
      matters: matters.filter((matter) =>
        matter.humansAndOrganizations?.some(
          (humanOrOrganization) =>
            humanOrOrganization.id === human.id &&
            humanOrOrganization.role === 'client',
        ),
      ),
    }));
  }, [humans, matters]);

  return {
    humansWithMatters,
    isLoading,
    pagination: {
      total: totalCount,
      pageSize,
      page,
      totalPages: Math.ceil(totalCount / pageSize) || 1,
    },
  };
};

/**
 * WARNING: This function was generated by Claude and is not yet fully tested.
 */
export const useOrganizationContacts = (
  page = 1,
  pageSize = 50,
  filters: WhereClause<OrganizationData>[] = [],
  sortBy: OrderByClause<OrganizationData>[] = [],
) => {
  const authSession = useAuthenticatedSession();
  const [organizations, setOrganizations] = useState<OrganizationDocument[]>(
    [],
  );
  const [totalCount, setTotalCount] = useState(0);
  const [matters, setMatters] = useState<MatterDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get the total count for pagination
  useEffect(() => {
    if (!authSession) return;

    const fetchTotalCount = async () => {
      try {
        // We need to apply the same filters to get accurate total count
        const snapshot = await authSession.db.getOrganizations(filters);

        setTotalCount(snapshot.size);
      } catch (error) {
        console.error('Error fetching total count:', error);
      }
    };

    fetchTotalCount();
  }, [authSession, JSON.stringify(filters)]);

  // Fetch the paginated data
  useEffect(() => {
    if (!authSession) return;
    setIsLoading(true);

    // Determine sort order with fallback to companyName
    const effectiveSortBy =
      sortBy.length > 0
        ? sortBy
        : [['companyName', 'asc'] as OrderByClause<OrganizationData>];

    // Calculate pagination limits for Firestore
    const pageSizeLimit = pageSize;
    const startAt = (page - 1) * pageSize;

    // Apply pagination
    if (startAt > 0) {
      // For Firestore, we need to get a document to use as a starting point
      getDocs(
        query(
          authSession.db.query.organizations(filters, effectiveSortBy),
          limit(startAt),
        ),
      )
        .then((snapshot) => {
          const lastVisible = snapshot.docs[snapshot.docs.length - 1];

          if (lastVisible) {
            // If we have a starting point, get the next page
            const paginatedQuery = query(
              query(
                authSession.db.query.organizations(filters, effectiveSortBy),
                startAfter(lastVisible),
              ),

              limit(pageSizeLimit),
            );

            const unsubscribeOrganizations = onSnapshot(
              paginatedQuery,
              (snapshot) => {
                const orgData = snapshot.docs.map((doc) => ({
                  ...doc.data(),
                  id: doc.id,
                }));
                setOrganizations(orgData);
                setIsLoading(false);
              },
              (error) => {
                console.error('Error fetching organizations:', error);
                setIsLoading(false);
              },
            );

            return () => unsubscribeOrganizations();
          } else {
            // If there are no results in the previous pages, return empty
            setOrganizations([]);
            setIsLoading(false);
            return () => {};
          }
        })
        .catch((error) => {
          console.error('Error with pagination:', error);
          setIsLoading(false);
          return () => {};
        });
    } else {
      // First page, no need for starting point
      const unsubscribeOrganizations = onSnapshot(
        query(
          authSession.db.query.organizations(filters, effectiveSortBy),
          limit(pageSizeLimit),
        ),
        (snapshot) => {
          const orgData = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          setOrganizations(orgData);
          setIsLoading(false);
        },
        (error) => {
          console.error('Error fetching organizations:', error);
          setIsLoading(false);
        },
      );

      const unsubscribeMatters = authSession.db.subscribeToMattersChanges(
        (snapshot) => {
          setMatters(
            snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
          );
        },
      );

      return () => {
        unsubscribeOrganizations();
        unsubscribeMatters();
      };
    }
  }, [
    authSession,
    page,
    pageSize,
    JSON.stringify(filters),
    JSON.stringify(sortBy),
  ]);

  const organizationsWithMatters = useMemo(() => {
    // See `usePeopleContacts` — only count client-role matters so the row's
    // count matches what the drawer will show.
    return organizations.map((organization) => ({
      ...organization,
      matters: matters.filter((matter) =>
        matter.humansAndOrganizations?.some(
          (humanOrOrganization) =>
            humanOrOrganization.id === organization.id &&
            humanOrOrganization.role === 'client',
        ),
      ),
    }));
  }, [organizations, matters]);

  return {
    organizationsWithMatters,
    isLoading,
    pagination: {
      total: totalCount,
      pageSize,
      page,
      totalPages: Math.ceil(totalCount / pageSize) || 1,
    },
  };
};

/**
 * Returns matters where the given contact is a client (role === 'client').
 *
 * Backed by `_autoGeneratedFields.clientContactIds`, an array on each matter
 * that the `updateAutoGeneratedFieldsOnMatterWritten` cloud function keeps in
 * sync with `humansAndOrganizations` filtered to `role === 'client'`. Querying
 * that field directly avoids fetching every matter and filtering client-side.
 *
 * The composite index for `clientContactIds array-contains <id>` combined
 * with the default `name asc` ordering applied by `useMatters` lives in
 * `firestore.indexes.json`; without it, production queries fail with
 * `FAILED_PRECONDITION` and the drawer would spin forever.
 */
export const useMattersForClient = (contactId: string | undefined) => {
  return useMatters({
    where: contactId
      ? [['_autoGeneratedFields.clientContactIds', 'array-contains', contactId]]
      : [],
    disabled: !contactId,
  });
};
